const SYSTEM_EXPORT_LIMITS = Object.freeze({
  maxProjectObjectsPerSystem: 20_000,
  maxWireframeItemsPerSystem: 5_000,
  maxStepItemsPerSystem: 5_000,
  maxCadExportItemsTotal: 500,
  maxPerformanceStatsPerSystem: 1_000,
  maxBoundaryNodesPerSystem: 1_000_000,
  maxArrayLength: 100_000,
  maxObjectKeys: 10_000,
  maxDepth: 32,
  maxStringLength: 65_536,
  maxExportLabelLength: 1_000,
  maxAssetPathLength: 2_048,
  dxfReadinessTimeoutMs: 30_000,
  stepReadinessTimeoutMs: 120_000
});

const FORBIDDEN_DATA_KEYS = new Set(['__proto__', 'prototype', 'constructor']);
const OWNER_FIELDS = Object.freeze([
  'owner',
  'ownerId',
  'ownerSystemId',
  'systemId',
  'systemKey',
  'systemType',
  'system',
  'sourceSystemId'
]);
const COMMON_PLACEMENT_FIELDS = Object.freeze([
  'label',
  'x',
  'y',
  'z',
  'rotationDeg',
  'scaleX',
  'scaleY',
  'scaleZ',
  'modelRotationXDeg',
  'modelRotationYDeg',
  'modelRotationZDeg',
  'originMode',
  'preciseScale'
]);
const WIREFRAME_EXPORT_FIELDS = Object.freeze([
  ...COMMON_PLACEMENT_FIELDS,
  'wireframeDxfPath'
]);
const STEP_EXPORT_FIELDS = Object.freeze([
  ...COMMON_PLACEMENT_FIELDS,
  'stepPath',
  'articleNumber',
  'color'
]);
const NUMERIC_PLACEMENT_FIELDS = new Set([
  'x',
  'y',
  'z',
  'rotationDeg',
  'scaleX',
  'scaleY',
  'scaleZ',
  'modelRotationXDeg',
  'modelRotationYDeg',
  'modelRotationZDeg'
]);
const ASSET_PATH_PATTERNS = Object.freeze({
  wireframe: /^\/api\/asset-objects\/([1-9][0-9]*)\/file\/dxfWireframe(?:\?[^#\s\\]*)?$/,
  step: /^\/api\/asset-objects\/([1-9][0-9]*)\/file\/source(?:\?[^#\s\\]*)?$/
});

class BoundaryValueError extends TypeError {
  constructor(code, message) {
    super(message);
    this.name = 'BoundaryValueError';
    this.code = code;
  }
}

function boundaryError(code, message) {
  return new BoundaryValueError(code, message);
}

function safeErrorText(error, field, fallback, maxLength = SYSTEM_EXPORT_LIMITS.maxExportLabelLength) {
  try {
    const value = error?.[field];
    if (typeof value === 'string' && value.trim()) return value.trim().slice(0, maxLength);
  } catch {
    // Auch geworfene Werte koennen fehlerhafte Getter oder Proxies sein.
  }
  if (field === 'message' && typeof error === 'string' && error.trim()) {
    return error.trim().slice(0, maxLength);
  }
  return fallback;
}

function frozenIssue(code, index = null, message = '') {
  const safeCode = typeof code === 'string' && code.trim()
    ? code.trim().slice(0, 120)
    : 'EXPORT_BOUNDARY_VALUE_INVALID';
  const safeMessage = typeof message === 'string' && message.trim()
    ? message.trim().slice(0, SYSTEM_EXPORT_LIMITS.maxExportLabelLength)
    : safeCode;
  return Object.freeze({ code: safeCode, index, message: safeMessage });
}

function cloneBudget(maxNodes = SYSTEM_EXPORT_LIMITS.maxBoundaryNodesPerSystem) {
  return { remainingNodes: Math.max(1, Number(maxNodes) || 1), ancestors: new WeakSet() };
}

function spendNode(budget) {
  budget.remainingNodes -= 1;
  if (budget.remainingNodes < 0) {
    throw boundaryError('EXPORT_BOUNDARY_NODE_LIMIT', 'Der Exportbeitrag ueberschreitet das Datenlimit.');
  }
}

function plainRecord(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  let prototype;
  try {
    prototype = Object.getPrototypeOf(value);
  } catch (error) {
    throw boundaryError(
      'EXPORT_BOUNDARY_OBJECT_INVALID',
      safeErrorText(error, 'message', 'Objektprototyp ist nicht lesbar.')
    );
  }
  return prototype === Object.prototype || prototype === null;
}

function ownDataEntries(value, options = {}) {
  let keys;
  try {
    keys = Reflect.ownKeys(value);
  } catch (error) {
    throw boundaryError(
      'EXPORT_BOUNDARY_KEYS_INVALID',
      safeErrorText(error, 'message', 'Objektschluessel sind nicht lesbar.')
    );
  }
  if (keys.length > (options.maxKeys ?? SYSTEM_EXPORT_LIMITS.maxObjectKeys)) {
    throw boundaryError('EXPORT_BOUNDARY_KEY_LIMIT', 'Der Exportbeitrag besitzt zu viele Felder.');
  }
  const entries = [];
  for (const key of keys) {
    if (typeof key !== 'string' || FORBIDDEN_DATA_KEYS.has(key)) {
      throw boundaryError('EXPORT_BOUNDARY_KEY_INVALID', 'Der Exportbeitrag besitzt einen ungueltigen Schluessel.');
    }
    let descriptor;
    try {
      descriptor = Object.getOwnPropertyDescriptor(value, key);
    } catch (error) {
      throw boundaryError(
        'EXPORT_BOUNDARY_DESCRIPTOR_INVALID',
        safeErrorText(error, 'message', 'Felddeskriptor ist nicht lesbar.')
      );
    }
    if (!descriptor || !Object.hasOwn(descriptor, 'value') || descriptor.enumerable !== true) {
      throw boundaryError('EXPORT_BOUNDARY_ACCESSOR_FORBIDDEN', `Exportfeld ${key} muss ein einfaches Datenfeld sein.`);
    }
    entries.push([key, descriptor.value]);
  }
  return entries;
}

function cloneDetachedPlainData(value, options = {}, depth = 0, budget = options.budget || cloneBudget(options.maxNodes)) {
  spendNode(budget);
  if (value === null || value === undefined || typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    if (value.length > (options.maxStringLength ?? SYSTEM_EXPORT_LIMITS.maxStringLength)) {
      throw boundaryError('EXPORT_BOUNDARY_STRING_LIMIT', 'Eine Zeichenkette im Exportbeitrag ist zu lang.');
    }
    return value;
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      throw boundaryError('EXPORT_BOUNDARY_NUMBER_INVALID', 'Exportdaten duerfen keine nicht-endlichen Zahlen enthalten.');
    }
    return value;
  }
  if (typeof value !== 'object') {
    throw boundaryError('EXPORT_BOUNDARY_VALUE_INVALID', 'Exportdaten muessen aus einfachen Daten bestehen.');
  }
  if (depth >= (options.maxDepth ?? SYSTEM_EXPORT_LIMITS.maxDepth)) {
    throw boundaryError('EXPORT_BOUNDARY_DEPTH_LIMIT', 'Der Exportbeitrag ist zu tief verschachtelt.');
  }
  if (budget.ancestors.has(value)) {
    throw boundaryError('EXPORT_BOUNDARY_CYCLE', 'Der Exportbeitrag darf keine Zyklen enthalten.');
  }

  let isArray;
  try {
    isArray = Array.isArray(value);
  } catch (error) {
    throw boundaryError(
      'EXPORT_BOUNDARY_ARRAY_INVALID',
      safeErrorText(error, 'message', 'Arraytyp ist nicht lesbar.')
    );
  }
  budget.ancestors.add(value);
  try {
    if (isArray) {
      let lengthDescriptor;
      try {
        lengthDescriptor = Object.getOwnPropertyDescriptor(value, 'length');
      } catch (error) {
        throw boundaryError(
          'EXPORT_BOUNDARY_DESCRIPTOR_INVALID',
          safeErrorText(error, 'message', 'Arraylaenge ist nicht lesbar.')
        );
      }
      const length = lengthDescriptor?.value;
      if (!Number.isSafeInteger(length) || length < 0
        || length > (options.maxArrayLength ?? SYSTEM_EXPORT_LIMITS.maxArrayLength)) {
        throw boundaryError('EXPORT_BOUNDARY_ARRAY_LIMIT', 'Ein Array im Exportbeitrag ist zu gross oder ungueltig.');
      }
      const clone = [];
      for (let index = 0; index < length; index += 1) {
        let descriptor;
        try {
          descriptor = Object.getOwnPropertyDescriptor(value, String(index));
        } catch (error) {
          throw boundaryError(
            'EXPORT_BOUNDARY_DESCRIPTOR_INVALID',
            safeErrorText(error, 'message', 'Arrayeintrag ist nicht lesbar.')
          );
        }
        if (!descriptor || !Object.hasOwn(descriptor, 'value') || descriptor.enumerable !== true) {
          throw boundaryError('EXPORT_BOUNDARY_ARRAY_INVALID', 'Exportarrays duerfen keine Luecken oder Getter enthalten.');
        }
        clone.push(cloneDetachedPlainData(descriptor.value, { ...options, budget }, depth + 1, budget));
      }
      let keys;
      try {
        keys = Reflect.ownKeys(value);
      } catch (error) {
        throw boundaryError(
          'EXPORT_BOUNDARY_KEYS_INVALID',
          safeErrorText(error, 'message', 'Arrayschluessel sind nicht lesbar.')
        );
      }
      if (keys.some(key => key !== 'length' && (!/^(?:0|[1-9][0-9]*)$/.test(String(key)) || Number(key) >= length))) {
        throw boundaryError('EXPORT_BOUNDARY_ARRAY_INVALID', 'Exportarrays duerfen keine Zusatzfelder enthalten.');
      }
      return Object.freeze(clone);
    }

    if (!plainRecord(value)) {
      throw boundaryError('EXPORT_BOUNDARY_OBJECT_INVALID', 'Exportdaten duerfen nur einfache Objekte enthalten.');
    }
    const clone = {};
    for (const [key, entry] of ownDataEntries(value, options)) {
      clone[key] = cloneDetachedPlainData(entry, { ...options, budget }, depth + 1, budget);
    }
    return Object.freeze(clone);
  } finally {
    budget.ancestors.delete(value);
  }
}

function normalizedOwnerCandidates(ownerId, aliases = []) {
  const values = [ownerId, ...(Array.isArray(aliases) ? aliases : [])]
    .map(value => String(value || '').trim())
    .filter(Boolean);
  return new Set(values.flatMap(value => [value, value.toLowerCase()]));
}

function hasOwnedDeclarations(record, ownerId, aliases = []) {
  const accepted = normalizedOwnerCandidates(ownerId, aliases);
  for (const field of OWNER_FIELDS) {
    if (!Object.hasOwn(record, field)) continue;
    const value = record[field];
    if (value === null || value === undefined || value === '') continue;
    if (typeof value !== 'string' && typeof value !== 'number') return false;
    const candidate = String(value).trim();
    if (!candidate || (!accepted.has(candidate) && !accepted.has(candidate.toLowerCase()))) return false;
  }
  return true;
}

function withAuthoritativeOwner(record, ownerId) {
  return Object.freeze({
    ...record,
    owner: ownerId,
    ownerId,
    ownerSystemId: ownerId,
    systemId: ownerId,
    systemKey: ownerId,
    systemType: ownerId,
    system: ownerId,
    sourceSystemId: ownerId
  });
}

function safeArrayLength(value) {
  try {
    if (!Array.isArray(value)) return null;
    const descriptor = Object.getOwnPropertyDescriptor(value, 'length');
    return Number.isSafeInteger(descriptor?.value) && descriptor.value >= 0 ? descriptor.value : null;
  } catch {
    return null;
  }
}

function arrayDataEntry(value, index) {
  let descriptor;
  try {
    descriptor = Object.getOwnPropertyDescriptor(value, String(index));
  } catch (error) {
    throw boundaryError(
      'EXPORT_BOUNDARY_DESCRIPTOR_INVALID',
      safeErrorText(error, 'message', 'Listeneintrag ist nicht lesbar.')
    );
  }
  if (!descriptor || !Object.hasOwn(descriptor, 'value') || descriptor.enumerable !== true) {
    throw boundaryError('EXPORT_BOUNDARY_ARRAY_INVALID', 'Exportlisten duerfen keine Luecken oder Getter enthalten.');
  }
  return descriptor.value;
}

function normalizeOwnedList(value, options = {}, normalizeEntry = entry => entry) {
  const ownerId = String(options.ownerId || '').trim();
  if (!ownerId) {
    return Object.freeze({ entries: Object.freeze([]), issues: Object.freeze([
      frozenIssue('EXPORT_OWNER_INVALID', null, 'Der Export-Owner fehlt.')
    ]) });
  }
  const length = safeArrayLength(value);
  if (length === null) {
    return Object.freeze({ entries: Object.freeze([]), issues: Object.freeze([
      frozenIssue('EXPORT_LIST_INVALID', null, 'Der System-Hook muss ein einfaches Array liefern.')
    ]) });
  }
  const maxItems = Math.max(0, Number(options.maxItems) || 0);
  const count = Math.min(length, maxItems);
  const entries = [];
  const issues = [];
  const budget = cloneBudget(options.maxNodes);
  if (length > maxItems) {
    issues.push(frozenIssue('EXPORT_ITEM_LIMIT', maxItems, `Maximal ${maxItems} Eintraege pro System sind erlaubt.`));
  }
  for (let index = 0; index < count; index += 1) {
    try {
      const rawEntry = arrayDataEntry(value, index);
      const detached = cloneDetachedPlainData(rawEntry, { budget }, 0, budget);
      if (!plainRecord(detached)) {
        throw boundaryError('EXPORT_ITEM_INVALID', 'Ein Exportlisteneintrag muss ein einfaches Objekt sein.');
      }
      if (!hasOwnedDeclarations(detached, ownerId, options.ownerAliases)) {
        throw boundaryError('EXPORT_OWNER_MISMATCH', 'Der Exportlisteneintrag deklariert einen fremden Owner.');
      }
      const normalized = normalizeEntry(detached, index);
      if (!plainRecord(normalized)) {
        throw boundaryError('EXPORT_ITEM_INVALID', 'Der normalisierte Exportlisteneintrag ist ungueltig.');
      }
      entries.push(withAuthoritativeOwner(normalized, ownerId));
    } catch (error) {
      issues.push(frozenIssue(
        safeErrorText(error, 'code', 'EXPORT_ITEM_INVALID', 120),
        index,
        safeErrorText(error, 'message', 'Exportlisteneintrag wurde verworfen.')
      ));
    }
  }
  return Object.freeze({ entries: Object.freeze(entries), issues: Object.freeze(issues) });
}

function assertShortString(value, field, options = {}) {
  if (typeof value !== 'string') {
    throw boundaryError('EXPORT_FIELD_INVALID', `${field} muss eine Zeichenkette sein.`);
  }
  const normalized = value.trim();
  if (!normalized || normalized.length > (options.maxLength ?? SYSTEM_EXPORT_LIMITS.maxExportLabelLength)) {
    throw boundaryError('EXPORT_FIELD_INVALID', `${field} ist leer oder zu lang.`);
  }
  return normalized;
}

function assertAssetApiPath(value, kind) {
  const path = assertShortString(value, kind === 'step' ? 'stepPath' : 'wireframeDxfPath', {
    maxLength: SYSTEM_EXPORT_LIMITS.maxAssetPathLength
  });
  const match = ASSET_PATH_PATTERNS[kind]?.exec(path);
  const assetId = match ? Number(match[1]) : NaN;
  if (!match || !Number.isSafeInteger(assetId) || assetId <= 0) {
    throw boundaryError(
      'EXPORT_ASSET_PATH_INVALID',
      `${kind === 'step' ? 'STEP' : 'Wireframe'}-Quellen muessen sichere Asset-API-Pfade verwenden.`
    );
  }
  return path;
}

function normalizePlacementEntry(record, fields, kind) {
  const normalized = {};
  for (const field of fields) {
    if (!Object.hasOwn(record, field) || record[field] === undefined || record[field] === null) continue;
    const value = record[field];
    if (NUMERIC_PLACEMENT_FIELDS.has(field)) {
      if (typeof value !== 'number' || !Number.isFinite(value) || Math.abs(value) > 1_000_000_000) {
        throw boundaryError('EXPORT_FIELD_INVALID', `${field} muss eine endliche Zahl im erlaubten Bereich sein.`);
      }
      normalized[field] = value;
    } else if (field === 'preciseScale') {
      if (typeof value !== 'boolean') throw boundaryError('EXPORT_FIELD_INVALID', 'preciseScale muss boolesch sein.');
      normalized[field] = value;
    } else if (field === 'originMode') {
      if (value !== 'center' && value !== 'corner') {
        throw boundaryError('EXPORT_FIELD_INVALID', 'originMode ist ungueltig.');
      }
      normalized[field] = value;
    } else if (field === 'label' || field === 'articleNumber') {
      normalized[field] = assertShortString(value, field);
    } else if (field === 'color') {
      if (typeof value !== 'string' || !/^#[0-9a-f]{6}(?:[0-9a-f]{2})?$/i.test(value)) {
        throw boundaryError('EXPORT_FIELD_INVALID', 'color muss eine Hex-Farbe sein.');
      }
      normalized[field] = value;
    } else if (field === 'wireframeDxfPath') {
      normalized[field] = assertAssetApiPath(value, 'wireframe');
    } else if (field === 'stepPath') {
      normalized[field] = assertAssetApiPath(value, 'step');
    }
  }
  const requiredPath = kind === 'step' ? 'stepPath' : 'wireframeDxfPath';
  if (!normalized[requiredPath]) {
    throw boundaryError('EXPORT_ASSET_PATH_INVALID', `${requiredPath} fehlt.`);
  }
  return normalized;
}

function normalizeOwnedProjectObjects(value, options = {}) {
  return normalizeOwnedList(value, {
    ...options,
    maxItems: options.maxItems ?? SYSTEM_EXPORT_LIMITS.maxProjectObjectsPerSystem,
    maxNodes: options.maxNodes ?? SYSTEM_EXPORT_LIMITS.maxBoundaryNodesPerSystem
  });
}

function normalizeOwnedWireframeExportItems(value, options = {}) {
  return normalizeOwnedList(value, {
    ...options,
    maxItems: options.maxItems ?? SYSTEM_EXPORT_LIMITS.maxWireframeItemsPerSystem,
    maxNodes: options.maxNodes ?? SYSTEM_EXPORT_LIMITS.maxBoundaryNodesPerSystem
  }, entry => normalizePlacementEntry(entry, WIREFRAME_EXPORT_FIELDS, 'wireframe'));
}

function normalizeOwnedStepExportItems(value, options = {}) {
  return normalizeOwnedList(value, {
    ...options,
    maxItems: options.maxItems ?? SYSTEM_EXPORT_LIMITS.maxStepItemsPerSystem,
    maxNodes: options.maxNodes ?? SYSTEM_EXPORT_LIMITS.maxBoundaryNodesPerSystem
  }, entry => normalizePlacementEntry(entry, STEP_EXPORT_FIELDS, 'step'));
}

function normalizeOwned3DPerformanceStats(value, options = {}) {
  const length = safeArrayLength(value);
  const source = length === null ? [value] : value;
  return normalizeOwnedList(source, {
    ...options,
    maxItems: options.maxItems ?? SYSTEM_EXPORT_LIMITS.maxPerformanceStatsPerSystem,
    maxNodes: options.maxNodes ?? SYSTEM_EXPORT_LIMITS.maxBoundaryNodesPerSystem
  });
}

function detachedBoundaryValue(value, options = {}) {
  try {
    return Object.freeze({
      value: cloneDetachedPlainData(value, {
        maxNodes: options.maxNodes ?? SYSTEM_EXPORT_LIMITS.maxBoundaryNodesPerSystem
      }),
      issue: null
    });
  } catch (error) {
    return Object.freeze({
      value: null,
      issue: frozenIssue(
        safeErrorText(error, 'code', 'EXPORT_BOUNDARY_VALUE_INVALID', 120),
        null,
        safeErrorText(error, 'message', 'Exportwert wurde verworfen.')
      )
    });
  }
}

function normalizeExcludedSystemIds(value) {
  const length = safeArrayLength(value);
  if (length === null) return Object.freeze([]);
  const ids = [];
  const max = Math.min(length, 10_000);
  for (let index = 0; index < max; index += 1) {
    try {
      const candidate = arrayDataEntry(value, index);
      if (typeof candidate !== 'string' && typeof candidate !== 'number') continue;
      const id = String(candidate).trim();
      if (id && id.length <= 120) ids.push(id);
    } catch {
      // Ein fehlerhafter Ausschlusseintrag erweitert niemals die Berechtigung.
    }
  }
  return Object.freeze([...new Set(ids)]);
}

function applyOwnedCadExportBudget(value, options = {}) {
  const source = Array.isArray(value) ? value : [];
  const requestedLimit = Number(options.maxItems);
  const maxItems = Number.isSafeInteger(requestedLimit) && requestedLimit > 0
    ? Math.min(SYSTEM_EXPORT_LIMITS.maxCadExportItemsTotal, requestedLimit)
    : SYSTEM_EXPORT_LIMITS.maxCadExportItemsTotal;
  const countsByOwner = new Map();
  const validEntries = [];
  for (const entry of source) {
    const owner = typeof entry?.owner === 'string' ? entry.owner.trim() : '';
    const systemId = typeof entry?.systemId === 'string' ? entry.systemId.trim() : '';
    if (!owner || owner !== systemId) continue;
    validEntries.push(entry);
    countsByOwner.set(owner, (countsByOwner.get(owner) || 0) + 1);
  }
  const excludedOwners = new Set(
    [...countsByOwner.entries()]
      .filter(([, count]) => count > maxItems)
      .map(([owner]) => owner)
  );
  let includedItemCount = validEntries.length
    - [...excludedOwners].reduce((total, owner) => total + (countsByOwner.get(owner) || 0), 0);

  // Das Backendlimit ist projektweit. Ein einzelner grosser Owner darf deshalb
  // nicht alle kleineren, unabhaengigen Systembeitraege blockieren. Solange das
  // Restbudget ueberschritten ist, wird der groesste Owner als Ganzes entfernt;
  // bei Gleichstand entscheidet die kanonische Owner-ID deterministisch.
  while (includedItemCount > maxItems) {
    const candidate = [...countsByOwner.entries()]
      .filter(([owner]) => !excludedOwners.has(owner))
      .sort(([ownerA, countA], [ownerB, countB]) => (
        countB - countA || ownerA.localeCompare(ownerB)
      ))[0];
    if (!candidate) break;
    excludedOwners.add(candidate[0]);
    includedItemCount -= candidate[1];
  }

  const excludedOwnerIds = Object.freeze([...excludedOwners].sort());
  const withinOwnerLimit = validEntries.filter(entry => !excludedOwners.has(entry.owner));
  const totalLimitExceeded = withinOwnerLimit.length > maxItems;
  return Object.freeze({
    items: Object.freeze(totalLimitExceeded ? [] : withinOwnerLimit),
    maxItems,
    totalLimitExceeded,
    totalItemCount: validEntries.length,
    excludedItemCount: validEntries.length - withinOwnerLimit.length,
    excludedOwnerIds
  });
}

function frozenBoundaryError(error) {
  let name = 'Error';
  let code = null;
  let message = 'Unbekannter System-Hook-Fehler.';
  try {
    if (typeof error?.name === 'string' && error.name.trim()) name = error.name.trim().slice(0, 120);
  } catch {
    // Fehlerobjekte duerfen selbst fehlerhafte Getter besitzen.
  }
  try {
    if (typeof error?.code === 'string' && error.code.trim()) code = error.code.trim().slice(0, 120);
  } catch {
    // Siehe oben.
  }
  try {
    if (typeof error?.message === 'string' && error.message.trim()) {
      message = error.message.trim().slice(0, SYSTEM_EXPORT_LIMITS.maxExportLabelLength);
    } else if (typeof error === 'string' && error.trim()) {
      message = error.trim().slice(0, SYSTEM_EXPORT_LIMITS.maxExportLabelLength);
    }
  } catch {
    // Siehe oben.
  }
  return Object.freeze({ name, code, message });
}

export {
  SYSTEM_EXPORT_LIMITS,
  applyOwnedCadExportBudget,
  detachedBoundaryValue,
  frozenBoundaryError,
  normalizeExcludedSystemIds,
  normalizeOwned3DPerformanceStats,
  normalizeOwnedProjectObjects,
  normalizeOwnedStepExportItems,
  normalizeOwnedWireframeExportItems
};
